import React, { useEffect } from "react";
import { RouteComponentProps, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Heading, RoundButton, Select } from "@foris/avocado-suite";
import { Dimension } from "@models/IDimension";
import cx from "classnames";
import Layout from "@common/layout/Layout";
import useNavigationUrl from "@common/hooks/useNavigationUrl";
import { Header } from "@common/components";
import { getOptionsFilter } from "@common/layout/components/context-search/utils/contextOptions.util";
import { PathParams } from "@edhtypes/general";
import {
  setAdvancedSearchDimension,
  useAdvancedSearchStore,
} from "@context/useAdvancedSearchStore";
import { AdvanceSearchDimensionRegistry } from "./types";
import css from "./advanceSearch.module.scss";

interface AdvanceSearchProps extends RouteComponentProps {
  dimensionComponents: AdvanceSearchDimensionRegistry;
}

const getMatchedDimension = (dimension: string) => {
  const normalizedDimension = dimension === "link" ? Dimension.league : dimension;
  return getOptionsFilter().find(value => value.value === normalizedDimension) || null;
};

const AdvanceSearch: React.FC<AdvanceSearchProps> = props => {
  const { dimension }: PathParams = useParams();
  const { adaptedHomeUrl } = useNavigationUrl();
  const selectedDimension = useAdvancedSearchStore(state => state.dimension);

  useEffect(() => {
    if (dimension) {
      setAdvancedSearchDimension(getMatchedDimension(dimension)?.value);
    }
  }, [dimension]);

  const { t } = useTranslation();

  const SelectComponent = selectedDimension ? props.dimensionComponents[selectedDimension] : null;

  return (
    <Layout contextSearch={false}>
      <Header
        title={t("advanced-search.breadcrumb.current")}
        breadcrumbItems={[
          {
            path: adaptedHomeUrl(),
            title: t("advanced-search.breadcrumb.previous"),
          },
          {
            title: t("advanced-search.breadcrumb.current"),
          },
        ]}
      />
      <section className={cx(css.cntAdvanceSearch)}>
        <Heading className={css.cntAdvanceSearch_title} type="h2">
          <RoundButton icon="chevron-left" variant="ghost" onClick={() => props.history.goBack()} />
          {t("advanced-search.title")}
        </Heading>

        <Select
          className={css.cntAdvanceSearch_select}
          label={t("advanced-search.select-dimension.label")}
          placeholder={t("advanced-search.select-dimension.placeholder")}
          value={getMatchedDimension(selectedDimension)}
          options={getOptionsFilter()}
          onChange={(value: any) => {
            setAdvancedSearchDimension(value?.value);
          }}
        />

        {SelectComponent && <SelectComponent />}
      </section>
    </Layout>
  );
};

export default AdvanceSearch;
